import 'react-toastify/dist/ReactToastify.css';
import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { blobToJson } from '@zelos/shared/lib/file-util';
import { TypedApiError } from '@zelos/shared/lib/typed-api-error';
import type { ToastOptions } from 'react-toastify';
import { toast, ToastContainer } from 'react-toastify';
import { notoSansKrFontFamily } from './fonts';
import { colors } from '@/constants/colors';

type ShowToastProps = {
  text: string,
  type?: ToastType,
  options?: ToastOptions,
};

type ToastType = 'normal' | 'error';

const toastBreakpoint = '480px';

const toastTypeCss: { [key in ToastType]: SerializedStyles } = Object.freeze({
  normal: css`
    background-color: ${colors.black900};
    color: ${colors.white};
  `,
  error: css`
    background-color: ${colors.red25};
    color: ${colors.danger};
  `,
});

const ToastContainerWrapper = styled.div`
.Toastify__toast-container {
  z-index: 99999;
  display: grid;
  width: auto;
  min-width: 320px;
  place-items: center;
  @media (max-width: ${toastBreakpoint}) {
    top: 1em;
    width: 100vw;
    min-width: 0
  }
}
.Toastify__toast {
  min-height: 0;
  padding: 0;
  border-radius: 10px;
  @media (max-width: ${toastBreakpoint}) {
    max-width: 90vw;
    margin-bottom: 1rem;
  }
}
.Toastify__toast-body {
  min-height: 48px;
  padding: 12px 24px;
  margin: 0;
  font-family: ${notoSansKrFontFamily};
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  text-align: center;
  white-space: pre-wrap;
  word-break: keep-all;
  @media (max-width: ${toastBreakpoint}) {
    max-width: 90vw;
  }
}
${Object.values(toastTypeCss).map(({ name, styles }) => `._${name} {${styles}}`).join('\n')}
`;

export const ToastBaseContainer = () => (
  <ToastContainerWrapper>
    <ToastContainer
      position="top-center"
      autoClose={2000}
      hideProgressBar
      closeButton={false}
      draggable={false}
    />
  </ToastContainerWrapper>
);

export const showToast = ({ text, type = 'normal', options }: ShowToastProps) => {
  const option: ToastOptions = {
    bodyClassName: `_${toastTypeCss[type].name}`,
    ...options,
  };
  toast(text, option);
};

export const createErrorToast = (typedApiError?: TypedApiError | null, choice: 'detail' | 'nonFieldErrors' | 'both' = 'both') => {
  if (!typedApiError) return;

  if (typedApiError.body instanceof Blob) {
    blobToJson(typedApiError.body).then((parsedBody) => {
      createErrorToast(new TypedApiError({
        ...typedApiError,
        body: parsedBody,
      }), choice);
    });
    return;
  }

  const {
    detail,
    nonFieldErrors,
  } = typedApiError;
  const errorMessages: string[] = [];

  (choice === 'detail' || choice === 'both') &&
  detail && errorMessages.push(...detail);

  (choice === 'nonFieldErrors' || choice === 'both') &&
  errorMessages.push(...nonFieldErrors);

  for (const errorMessage of errorMessages) {
    showToast({ text: errorMessage, type: 'error' });
  }
};

export { ToastOptions };
